import React, { useState } from 'react'
import Logo from './shared/Logo'
import { 
  TbBrandInstagram, 
  TbBrandLinkedin, 
  TbBrandTiktok, 
  TbBrandTwitter,
  TbArrowUpRight,
  TbHeart,
  TbSend,
  TbChevronRight
} from 'react-icons/tb'
import { Link } from 'gatsby'
import { motion } from 'framer-motion'
import { isValidEmail } from '../utils/validate'

export default function Footer({
  data = undefined as any
}) {

  const [ email, setEmail ] = useState('')
  const [ error, setError ] = useState('')
  const [ success, setSuccess ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)

  const features = data?.data?.footerFeatures?.nodes || [];
  const compares = data?.data?.footerCompares?.nodes || [];
  const industries = data?.data?.footerIndustries?.nodes || [];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  // Social media links with hover effects
  const socialLinks = [
    { icon: <TbBrandTwitter />, url: "https://twitter.com/PipelimeAI", label: "Twitter" },
    { icon: <TbBrandLinkedin />, url: "https://www.linkedin.com/company/pipelime-ai/about/", label: "LinkedIn" },
    { icon: <TbBrandInstagram />, url: "https://www.instagram.com/pipelime.ai/", label: "Instagram" },
    { icon: <TbBrandTiktok />, url: "https://www.tiktok.com/@pipelime.ai?_t=8k0s1Cl4vdR&_r=1", label: "TikTok" }
  ];

  const encode = (data: any) => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]),
      )
      .join('&');
  }

  // Newsletter form submission handler
  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setSuccess(false);
    setError("");

    if (!isValidEmail(email.trim())) {
      setError("Please, enter a valid email");
      return false;
    }

    setIsLoading(true);
    if (!localStorage.getItem(`waitlist_${email}`)) {
      localStorage.setItem(`waitlist_${email}`, "1");
      fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 
          'form-name': "newsletter", 
          'email': email,
          'land_url': localStorage.getItem(`land_url`),
          'document.referrer': document.referrer
        }),
      });
    }
    
    setTimeout(() => {
      setSuccess(true);
      setIsLoading(false);
      setEmail('');
    }, 1900)

    return false;
  };

  return (
    <footer className="relative overflow-hidden">
      {/* Top decorative wave */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-0 transform rotate-180">
        <svg className="relative block w-full h-12 md:h-16 lg:h-20" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-black/90"></path>
        </svg>
      </div>

      {/* Main footer content */}
      <div className="bg-gradient-to-b from-black to-gray-900 pt-24 pb-12 px-6 lg:px-24 text-white relative z-10">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Gradient overlay */}
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(150,245,38,0.08),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(150,245,38,0.05),transparent_50%)]"></div>
          
          {/* Decorative circles */}
          <div className="absolute top-20 right-20 w-32 h-32 rounded-full bg-primary-500/5"></div>
          <div className="absolute bottom-40 left-20 w-40 h-40 rounded-full bg-primary-500/3"></div>
          
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]"></div>
        </div>

        <motion.div 
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px 0px" }}
          variants={containerVariants}
        >
          {/* Top section with logo, contact, and newsletter */}
          <motion.div 
            variants={itemVariants}
            className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8 mb-16 pb-16 border-b border-white/10"
          >
            {/* Logo and company info */}
            <div className="flex flex-col items-center lg:items-start gap-6">
              <Logo
                sizeClass="!w-[60px] !h-[60px] lg:!w-[50px] lg:!h-[50px]"
                textSizeClass="text-white text-2xl"
              />
              <p className="text-gray-300 max-w-xs text-center lg:text-left">
                Revolutionizing sales processes with AI-powered automation to help businesses grow faster and smarter.
              </p>
              <div className="flex gap-4">
                {socialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="bg-white/10 hover:bg-primary-500 text-white p-3 rounded-full transition-all duration-300 transform hover:scale-110 hover:rotate-3"
                  >
                    {React.cloneElement(social.icon, { className: 'text-xl' })}
                  </a>
                ))}
              </div>
            </div>

            {/* Contact information */}
            <div className="flex flex-col items-center lg:items-start gap-6">
              <h3 className="text-xl font-bold relative">
                Contact Us
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-primary-500 rounded-full"></span>
              </h3>
              <div className="flex flex-col gap-4 text-gray-300">
                <a 
                  className="hover:text-primary-300 transition-colors flex items-center gap-2" 
                  href="mailto:hello@pipelime.ai"
                >
                  <span className="bg-white/10 p-2 rounded-full">
                    <TbSend className="text-primary-300" />
                  </span>
                  hello@pipelime.ai
                </a>
                <p className="flex items-start gap-2">
                  <span className="bg-white/10 p-2 rounded-full mt-1">
                    <TbHeart className="text-primary-300" />
                  </span>
                  <span>
                    Reblox Solutions LLC<br/>
                    2222 Quail Roost Dr, Weston, FL 33327
                  </span>
                </p>
              </div>
            </div>

            {/* Newsletter subscription */}
            <div className="flex flex-col items-center lg:items-start gap-6">
              <h3 className="text-xl font-bold relative">
                Stay Updated
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-primary-500 rounded-full"></span>
              </h3>
              <p className="text-gray-300 max-w-xs text-center lg:text-left">
                Subscribe to our newsletter for the latest updates, tips, and special offers.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="w-full max-w-sm">
                <div className="relative">
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address" 
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleNewsletterSubmit(e)
                      }
                    }}
                    className="w-full bg-white/10 border border-white/20 rounded-full py-3 px-5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                  <button 
                    type="submit" 
                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 bg-primary-500 hover:bg-primary-600 text-white rounded-full p-2 transition-colors duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <TbChevronRight className="text-xl" />
                    )}
                  </button>
                </div>
                {error && (
                  <p className="text-red-400 text-sm mt-2 ml-2">{error}</p>
                )}
                {success && (
                  <p className="text-primary-400 text-sm mt-2 ml-2">Thank you for subscribing!</p>
                )}
              </form>
            </div>
          </motion.div>

          {/* Main links section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-16 mb-16">
            {/* Quick Menu */}
            <motion.div variants={itemVariants} className="flex flex-col gap-6">
              <h3 className="text-lg font-bold relative inline-block">
                Quick Menu
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-primary-500/70 rounded-full"></span>
              </h3>
              <div className="flex flex-col gap-3">
                <Link to="/" className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group">
                  Home
                  <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </Link>
                <Link to="/pricing" className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group">
                  Pricing
                  <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </Link>
                <Link to="/features" className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group">
                  Features
                  <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </Link>
                <Link to="/industries" className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group">
                  Industries
                  <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </Link>
                <Link to="/blogs" className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group">
                  Blog
                  <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </Link>
                <a href="https://help.pipelime.ai" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group">
                  Help
                  <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                </a>
              </div>
            </motion.div>

            {/* Features */}
            <motion.div variants={itemVariants} className="flex flex-col gap-6">
              <h3 className="text-lg font-bold relative inline-block">
                Features
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-primary-500/70 rounded-full"></span>
              </h3>
              <div className="flex flex-col gap-3">
                {features.map((item: any, index: number) => (
                  <a 
                    key={index}
                    href={`/feature/${item.slug}`} 
                    className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group"
                  >
                    {item.footerText}
                    <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Industries */}
            <motion.div variants={itemVariants} className="flex flex-col gap-6">
              <h3 className="text-lg font-bold relative inline-block">
                Industries
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-primary-500/70 rounded-full"></span>
              </h3>
              <div className="flex flex-col gap-3">
                {industries.map((item: any, index: number) => (
                  <a 
                    key={index}
                    href={`/industry/${item.slug}`} 
                    className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group"
                  >
                    {item.footerText}
                    <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Compare */}
            <motion.div variants={itemVariants} className="flex flex-col gap-6">
              <h3 className="text-lg font-bold relative inline-block">
                Compare
                <span className="absolute -bottom-2 left-0 w-8 h-1 bg-primary-500/70 rounded-full"></span>
              </h3>
              <div className="flex flex-col gap-3">
                {compares.map((item: any, index: number) => (
                  <a 
                    key={index}
                    href={`/alternative/${item.slug}`} 
                    className="text-gray-300 hover:text-primary-300 transition-colors flex items-center gap-2 group"
                  >
                    {item.footerText}
                    <TbArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-sm" />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Bottom section with copyright and legal links */}
          <motion.div 
            variants={itemVariants}
            className="pt-8 border-t border-white/10 flex flex-col lg:flex-row justify-between items-center gap-6"
          >
            <div className="text-gray-400 text-sm text-center lg:text-left">
              © {new Date().getFullYear()} <span className="text-primary-300">Reblox Solutions LLC</span>. All rights reserved.
            </div>
            
            <div className="flex flex-wrap gap-6 justify-center">
              <Link to="/terms" className="text-gray-400 hover:text-primary-300 text-sm transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-primary-300 text-sm transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-400 text-sm">
                Need help? <a className="text-primary-300 hover:text-primary-400 transition-colors" href="mailto:hello@pipelime.ai">Contact us</a>
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  )
}
